import { Component } from "react"
import { createPortal } from "react-dom"
import confetti from "canvas-confetti"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import StateConstants from "src/states/StateContstants"
import ApiHandler from "src/api/ApiHandler"
import PlanContent from "./PlanContent"
import "./PlanModal.css"
import "./PlanModalOverrides.css"
import { applyUserEntitlements } from "src/utils/LocalDevAccess"


interface PlanModalProps {
    stateHandler: EventAdaptingStateHandler
    apiHandler: ApiHandler
}

interface PlanModalState {
    isOpen: boolean
    showSuccess: boolean
    variant: "choice" | "full"
}


class PlanModal extends Component<PlanModalProps, PlanModalState> {
    constructor(props: PlanModalProps) {
        super(props)

        this.state = {
            isOpen: false,
            showSuccess: false,
            variant: "choice",
        }

        props.stateHandler.addListener(StateConstants.SHOW_PLAN_MODAL, (isOpen: boolean) => {
            if (isOpen) {
                const hasPending = !!(
                    props.stateHandler.get(StateConstants.PENDING_PAYMENT_INTENT_ID)
                    || localStorage.getItem("pendingPaymentIntentId")
                )
                if (hasPending) {
                    props.stateHandler.set(StateConstants.SHOW_PLAN_MODAL, false)
                    props.stateHandler.set(StateConstants.SHOW_LOGIN_MODAL, true)
                    return
                }
            }
            const variant = isOpen
                ? (props.stateHandler.get(StateConstants.PLAN_MODAL_VARIANT) || "choice")
                : "choice"
            this.setState({ isOpen, showSuccess: false, variant })
            document.body.style.overflow = isOpen ? "hidden" : ""
            document.documentElement.style.overflow = isOpen ? "hidden" : ""
        })
    }

    private confettiCanvas: HTMLCanvasElement | null = null

    showSuccess = () => {
        this.setState({ showSuccess: true })
        this.fireConfetti()
        this.refreshUserStatus()
    }

    async refreshUserStatus() {
        const maxAttempts = 5
        const delayMs = 2000

        for (let i = 0; i < maxAttempts; i++) {
            await new Promise(resolve => setTimeout(resolve, delayMs))
            this.props.apiHandler.clearCache()
            try {
                const userInfo = await this.props.apiHandler.getUserInfo()
                if (userInfo) {
                    applyUserEntitlements(
                        this.props.stateHandler,
                        userInfo?.features || [],
                        userInfo?.at || "basic",
                        userInfo?.free_detailed_valuation_credits_remaining ?? 0,
                        userInfo?.free_unlocked_property_ids ?? []
                    )
                    return
                }
            } catch (error) {
                // Retry
            }
        }
    }

    fireConfetti() {
        if (sessionStorage.getItem("confettiShown")) return
        sessionStorage.setItem("confettiShown", "true")

        if (!this.confettiCanvas) {
            this.confettiCanvas = document.createElement("canvas")
            this.confettiCanvas.style.position = "fixed"
            this.confettiCanvas.style.top = "0"
            this.confettiCanvas.style.left = "0"
            this.confettiCanvas.style.width = "100%"
            this.confettiCanvas.style.height = "100%"
            this.confettiCanvas.style.pointerEvents = "none"
            this.confettiCanvas.style.zIndex = "999999"
            document.body.appendChild(this.confettiCanvas)
        }

        const myConfetti = confetti.create(this.confettiCanvas, { resize: true })
        const duration = 3000
        const end = Date.now() + duration
        const frame = () => {
            myConfetti({ particleCount: 3, angle: 60, spread: 55, origin: { x: 0, y: 0.7 } })
            myConfetti({ particleCount: 3, angle: 120, spread: 55, origin: { x: 1, y: 0.7 } })
            if (Date.now() < end) {
                requestAnimationFrame(frame)
            } else {
                setTimeout(() => {
                    if (this.confettiCanvas) {
                        document.body.removeChild(this.confettiCanvas)
                        this.confettiCanvas = null
                    }
                }, 2000)
            }
        }
        frame()
    }

    close = () => {
        this.setState({ isOpen: false, showSuccess: false, variant: "choice" })
        this.props.stateHandler.set(StateConstants.SHOW_PLAN_MODAL, false)
        this.props.stateHandler.set(StateConstants.PLAN_MODAL_VARIANT, "choice")
        document.body.style.overflow = ""
        document.documentElement.style.overflow = ""
    }

    render() {
        if (!this.state.isOpen) return null

        const modalClass = "plan-modal plan-modal--next"

        return createPortal(
            <>
                <div className="plan-modal-backdrop" onClick={this.close} />
                <div className={modalClass}>
                    <button className="plan-modal-close" onClick={this.close}>&#10005;</button>
                    <div className="plan-modal-body">
                        <PlanContent
                            stateHandler={this.props.stateHandler}
                            apiHandler={this.props.apiHandler}
                            showSuccess={this.state.showSuccess}
                            onPaymentComplete={this.showSuccess}
                            variant={this.state.variant === "full" ? "page" : "modal"}
                        />
                    </div>
                </div>
            </>,
            document.body
        )
    }
}

export default PlanModal
